import axios from 'axios';

import {
  validateDeliveryHours,
  validateOverlappingPeriods,
  validateSameStartEndTime,
} from 'components/MenuManagement/MenuEdit/Hours/validation';
import { DEFAULT_SCHEDULE, SCHEDULE_DAY_OPTIONS } from 'constants/menuManagement';
import { cleanMenuSchedules } from 'helpers/menuManagementHours';
import { generateTempId } from 'helpers/utils';
import type { MenuUploadErrorType } from 'helpers/menuUploadAnalytics';

import type { MenuHour } from 'types/menuHours';

import type { BuildHour, BuildStatus, MenuType, Schedule } from './types';

const MIME_TO_BUILD_TYPE: Record<string, string> = {
  'application/pdf': 'file_pdf',
  'image/jpeg': 'file_image',
  'image/png': 'file_image',
};

/** The menu type the build endpoint expects. */
export function toBuildMenuType(menuType: MenuType): 'catering' | 'standard' {
  return menuType === 'takeout' ? 'standard' : 'catering';
}

export const getBuildType = (file: File): string => MIME_TO_BUILD_TYPE[file.type] || 'file_pdf';

export const URL_BUILD_TYPE = 'url';

const MAX_MENU_URL_LENGTH = 255;

// The field accepts bare domains, but MM and LIS both reject a URL with no scheme.
export const normalizeMenuUrl = (url: string): string => {
  const trimmed = url.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// Parsed, not regex-matched: a character class rejects legal chars like ' , ! $ and é.
export const isValidMenuUrl = (url: string): boolean => {
  const trimmed = url.trim();
  if (!trimmed || trimmed.length > MAX_MENU_URL_LENGTH) return false;

  try {
    const parsed = new URL(normalizeMenuUrl(trimmed));
    // The BE fetches this URL, so only allow web schemes and no embedded credentials.
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
    if (parsed.username || parsed.password) return false;
    // URL() accepts single-word hosts like "localhost"; a menu URL needs a TLD.
    return parsed.hostname.includes('.');
  } catch {
    return false;
  }
};

type MenuInfoErrorKey =
  'menuNameMaxLength' | 'menuNameMinLength' | 'menuNameRequired' | 'taxRateRequired';

/** The `MenuInfoStep` message key for each invalid info field. */
export function getMenuInfoErrorKeys({
  menuName,
  taxRate,
}: {
  menuName: string;
  taxRate: number | string;
}): { menuName?: MenuInfoErrorKey; taxRate?: MenuInfoErrorKey } {
  const errors: { menuName?: MenuInfoErrorKey; taxRate?: MenuInfoErrorKey } = {};
  const name = menuName.trim();

  if (!name) errors.menuName = 'menuNameRequired';
  else if (name.length < 3) errors.menuName = 'menuNameMinLength';
  else if (name.length > 50) errors.menuName = 'menuNameMaxLength';

  if (taxRate === '') errors.taxRate = 'taxRateRequired';

  return errors;
}

export function toTaxRate(taxRate: number | string): number {
  return typeof taxRate === 'number' ? taxRate : parseFloat(String(taxRate)) || 0;
}

export const createDefaultSchedules = (): Schedule[] =>
  SCHEDULE_DAY_OPTIONS.map(day => ({
    tempId: generateTempId(),
    dayIndex: day.value,
    startTime: DEFAULT_SCHEDULE.startTime,
    endTime: DEFAULT_SCHEDULE.endTime,
  }));

type SavedMenuHours = {
  deliveryHours?: MenuHour[];
  deliveryMatchesPickup?: boolean;
  itemHours?: MenuHour[];
};

/** Rows of saved hours as schedules, the earliest of each day first and the rest its periods. */
function toSchedules(hours: MenuHour[] | undefined, { keepIds }: { keepIds: boolean }): Schedule[] {
  const hoursByDay = new Map<string, MenuHour[]>();

  (hours ?? []).forEach(hour => {
    hoursByDay.set(hour.dayIndex, [...(hoursByDay.get(hour.dayIndex) ?? []), hour]);
  });

  return [...hoursByDay.values()].flatMap(dayHours =>
    [...dayHours]
      .sort((a, b) => a.startTime.localeCompare(b.startTime))
      .map(({ uuid, ...hour }, index) => ({
        ...hour,
        ...(keepIds && uuid && { uuid }),
        isPeriod: index > 0,
        tempId: generateTempId(),
      })),
  );
}

/**
 * The pickup and delivery schedules to edit for a saved menu, as the menu editor loads them:
 * delivery follows pickup unless the menu has its own delivery hours.
 */
export function getMenuSchedules({
  deliveryHours,
  deliveryMatchesPickup,
  itemHours,
}: SavedMenuHours): { deliverySchedules: Schedule[]; pickupSchedules: Schedule[] } {
  const hasOwnDelivery = deliveryMatchesPickup === false && !!deliveryHours;

  return {
    deliverySchedules: toSchedules(hasOwnDelivery ? deliveryHours : itemHours, {
      keepIds: hasOwnDelivery,
    }),
    pickupSchedules: toSchedules(itemHours, { keepIds: true }),
  };
}

/** Schedules as the saved menu hours the BE takes, without the deleted rows and local props. */
export function toSavedHours(schedules: Schedule[]): MenuHour[] {
  return cleanMenuSchedules(schedules) as MenuHour[];
}

export const schedulesToBuildHours = (schedules: Schedule[]): BuildHour[] =>
  schedules
    .filter(s => !s.isDeleted)
    .map(({ dayIndex, startTime, endTime }) => ({ dayIndex, startTime, endTime }));

type MenuHoursErrorKey =
  | 'deliveryHoursOverlapping'
  | 'deliveryHoursSameStartEnd'
  | 'deliveryHoursWithinPickup'
  | 'pickupHoursOverlapping'
  | 'pickupHoursSameStartEnd';

type MenuHours = {
  deliverySchedules: Schedule[];
  pickupSchedules: Schedule[];
};

/** Whether two schedules hold the same hours row for row, ignoring deleted rows. */
export function areSchedulesEquivalent(first: Schedule[], second: Schedule[]): boolean {
  const a = first.filter(schedule => !schedule.isDeleted);
  const b = second.filter(schedule => !schedule.isDeleted);

  return (
    a.length === b.length &&
    a.every(
      (schedule, index) =>
        schedule.dayIndex === b[index].dayIndex &&
        schedule.startTime === b[index].startTime &&
        schedule.endTime === b[index].endTime,
    )
  );
}

/** The `MenuInfoStep` message key for each invalid hours section. */
export function getMenuHoursErrorKeys({ deliverySchedules, pickupSchedules }: MenuHours): {
  deliverySchedule?: MenuHoursErrorKey;
  pickupSchedule?: MenuHoursErrorKey;
} {
  const errors: { deliverySchedule?: MenuHoursErrorKey; pickupSchedule?: MenuHoursErrorKey } = {};

  const pickupOverlaps = validateOverlappingPeriods(pickupSchedules, 'pickup');
  const pickupSameTime = validateSameStartEndTime(pickupSchedules, 'pickup');

  if (pickupOverlaps.length > 0) errors.pickupSchedule = 'pickupHoursOverlapping';
  else if (pickupSameTime.length > 0) errors.pickupSchedule = 'pickupHoursSameStartEnd';

  const deliveryOverlaps = validateOverlappingPeriods(deliverySchedules, 'delivery');
  const deliverySameTime = validateSameStartEndTime(deliverySchedules, 'delivery');
  const deliveryOutsidePickup = areSchedulesEquivalent(deliverySchedules, pickupSchedules)
    ? []
    : validateDeliveryHours(pickupSchedules, deliverySchedules);

  if (deliveryOverlaps.length > 0) errors.deliverySchedule = 'deliveryHoursOverlapping';
  else if (deliveryOutsidePickup.length > 0) errors.deliverySchedule = 'deliveryHoursWithinPickup';
  else if (deliverySameTime.length > 0) errors.deliverySchedule = 'deliveryHoursSameStartEnd';

  return errors;
}

/** Pickup and delivery hours for the build request. */
export function getMenuBuildHours({ deliverySchedules, pickupSchedules }: MenuHours): {
  deliveryHours: BuildHour[];
  pickupHours: BuildHour[];
} {
  const pickupHours = schedulesToBuildHours(pickupSchedules);

  return {
    deliveryHours: areSchedulesEquivalent(deliverySchedules, pickupSchedules)
      ? pickupHours
      : schedulesToBuildHours(deliverySchedules),
    pickupHours,
  };
}

type MenuBuildErrorResponse = {
  detail?: string;
};

// Returns undefined if the error has no readable detail.
export function getMenuBuildRejectionReason(error: unknown): string | undefined {
  if (!axios.isAxiosError<MenuBuildErrorResponse>(error)) return undefined;
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : undefined;
}

const STATUS_ORDER: BuildStatus[] = [
  'started',
  'file_uploaded',
  'file_processed',
  'menu_created',
  'menu_inserted',
  'finished',
];

/** The later of two build statuses; a failure wins over any progress. */
export function furthestBuildStatus(
  a: BuildStatus | null,
  b: BuildStatus | null,
): BuildStatus | null {
  if (!a) return b;
  if (!b) return a;
  if (a === 'failed' || b === 'failed') return 'failed';

  return STATUS_ORDER.indexOf(a) >= STATUS_ORDER.indexOf(b) ? a : b;
}

type MenuBuildFailure = {
  errorType: MenuUploadErrorType;
  messageKey: 'buildInProgress' | 'invalidRequest' | 'rateLimitExceeded' | 'uploadError';
  rejectionReason?: string;
};

/** Maps a failed upload or build start to its analytics type and the message to show. */
export function getMenuBuildFailure(error: unknown, stage: 'start' | 'upload'): MenuBuildFailure {
  const status = (error as { response?: { status?: number } })?.response?.status;

  if (status === 409) return { errorType: 'build_in_progress', messageKey: 'buildInProgress' };
  if (status === 429) return { errorType: 'rate_limited', messageKey: 'rateLimitExceeded' };
  if (status === 400) {
    return {
      errorType: 'invalid_request',
      messageKey: 'invalidRequest',
      rejectionReason: getMenuBuildRejectionReason(error),
    };
  }

  return {
    errorType: stage === 'upload' ? 'file_upload_failed' : 'submit_failed',
    messageKey: 'uploadError',
  };
}
